import {
  followUpComplete,
  type TrainingAnswer,
  type TrainingAnswers,
  type TrainingPlayerPage,
} from '@hcc/types';

/**
 * True when the page has a complete-enough answer to allow moving on.
 * Slides are always "answered"; matching needs every target filled.
 * Question groups and text groups answer per row, and follow-ups depend on
 * the other pages' answers, so use pageComplete for those.
 */
export function isAnswered(page: TrainingPlayerPage, answer: TrainingAnswer | undefined): boolean {
  switch (page.type) {
    case 'slide':
      return true;
    case 'question_group':
    case 'text_group':
    case 'follow_up':
      // Rows carry their own answers (or the page needs the whole deck);
      // never keyed by a single answer under the page id.
      return false;
    case 'single_choice':
      return typeof answer === 'string' && answer !== '';
    case 'multi_select':
      return Array.isArray(answer) && answer.length > 0;
    case 'true_false':
      return typeof answer === 'boolean';
    case 'short_text':
      return typeof answer === 'string' && answer.trim() !== '';
    case 'categorize': {
      if (typeof answer !== 'object' || answer === null || Array.isArray(answer)) return false;
      const placed = answer as Record<string, string>;
      return page.items.every((it) => typeof placed[it.id] === 'string');
    }
    case 'ordering':
      return Array.isArray(answer) && answer.length === page.items.length;
    case 'hotspot':
      return (
        typeof answer === 'object' &&
        answer !== null &&
        !Array.isArray(answer) &&
        typeof (answer as { x?: unknown }).x === 'number'
      );
    case 'matching': {
      if (typeof answer !== 'object' || answer === null || Array.isArray(answer)) return false;
      const placed = answer as Record<string, string>;
      return page.targets.every((t) => typeof placed[t.id] === 'string');
    }
  }
}

function textGiven(answer: TrainingAnswer | undefined): boolean {
  return typeof answer === 'string' && answer.trim() !== '';
}

/**
 * Whether a whole page is done: every required question on it is answered.
 * Optional questions (the free-text follow-up at the end of an assessment)
 * never hold the learner up. A follow-up page needs the rest of the deck to
 * know which questions it is asking about; without it, it counts as done.
 */
export function pageComplete(
  page: TrainingPlayerPage,
  answers: TrainingAnswers,
  pages?: TrainingPlayerPage[],
): boolean {
  switch (page.type) {
    case 'slide':
      return true;
    case 'question_group':
      return page.rows.every(
        (r) => r.optional || (typeof answers[r.id] === 'string' && answers[r.id] !== ''),
      );
    case 'text_group':
      return page.rows.every((r) => r.optional || textGiven(answers[r.id]));
    case 'follow_up':
      return pages ? followUpComplete(pages, answers, page) : true;
    default:
      return page.optional || isAnswered(page, answers[page.id]);
  }
}

/** One question of a player page list, flattened out of any group. */
export interface PlayerQuestionRef {
  id: string;
  points: number;
  optional?: boolean;
}

/**
 * Every question in the deck, in page order, with group rows flattened.
 * Drives the question numbering and the "answered everything" gate, which
 * both count questions rather than pages. Follow-up pages contribute none:
 * their questions only exist once earlier answers are in.
 */
export function playerQuestions(pages: TrainingPlayerPage[]): PlayerQuestionRef[] {
  return pages.flatMap((p) => {
    if (p.type === 'slide' || p.type === 'follow_up') return [];
    if (p.type === 'question_group' || p.type === 'text_group') {
      return p.rows.map((r) => ({
        id: r.id,
        points: r.points,
        ...(r.optional ? { optional: true as const } : {}),
      }));
    }
    return [{ id: p.id, points: p.points, ...(p.optional ? { optional: true as const } : {}) }];
  });
}

/** How many questions a page carries (0 for a slide or follow-up, rows for a group). */
export function pageQuestionCount(page: TrainingPlayerPage): number {
  if (page.type === 'slide' || page.type === 'follow_up') return 0;
  return page.type === 'question_group' || page.type === 'text_group' ? page.rows.length : 1;
}

/** The questions of a player page list, in order (slides skipped). */
export function questionPages(pages: TrainingPlayerPage[]): TrainingPlayerPage[] {
  return pages.filter((p) => p.type !== 'slide');
}
